<template>
  <section id="dashboard-sifnf">
    <b-overlay :show="isLoading" rounded="sm" spinner-variant="primary">
      <div class="d-flex align-items-center justify-content-end mb-1 dashboard-cache-status">
        <small v-if="isRefreshing" class="text-muted mr-1">
          Обновление данных… обычно занимает несколько минут
        </small>
        <small
          v-else-if="cachedAt"
          class="text-muted mr-1"
          title="Игры, финансы, ретеншн и отзывы берутся из снапшота Planfix (пересобирается раз в час); контент и очереди — живые"
        >
          Данные на {{ formatCachedAt(cachedAt) }}
        </small>
        <b-button
          v-if="$can('manage', 'dashboard')"
          variant="outline-primary"
          size="sm"
          :disabled="isRefreshing"
          title="Пересобрать снапшот из Planfix"
          @click="refresh"
        >
          <b-spinner v-if="isRefreshing" small class="mr-50" />
          <feather-icon v-else icon="RefreshCwIcon" class="mr-50" />
          Обновить
        </b-button>
      </div>

      <template v-if="data">
        <!-- Ряд 1: медаль + KPI -->
        <b-row class="match-height">
          <b-col xl="4" md="6">
            <dashboard-profit-medal :business="data.business" />
          </b-col>
          <b-col xl="8" md="6">
            <dashboard-kpi-cards :business="data.business" :feedback="data.feedback" />
          </b-col>
        </b-row>

        <!-- Ряд 2: ближайшие игры + недельный график -->
        <b-row class="match-height">
          <b-col lg="4">
            <dashboard-upcoming-games :upcoming="data.upcoming" />
          </b-col>
          <b-col lg="8">
            <dashboard-weekly-chart :points="data.business && data.business.points" />
          </b-col>
        </b-row>

        <!-- Ряд 2.5: план/факт игр Planfix -->
        <b-row class="match-height">
          <b-col lg="12">
            <dashboard-plan-fact :plan-fact="data.games_plan_fact" />
          </b-col>
        </b-row>

        <!-- Ряд 3: ретеншн + отзывы -->
        <b-row class="match-height">
          <b-col lg="6">
            <dashboard-retention-chart :retention="data.retention" />
          </b-col>
          <b-col lg="6">
            <dashboard-feedback :feedback="data.feedback" />
          </b-col>
        </b-row>

        <!-- Ряд 4: топы + пакеты в работе -->
        <b-row class="match-height">
          <b-col lg="4" md="6">
            <dashboard-top-table
              title="Топ ведущих по играм"
              :rows="data.business && data.business.top_hosts"
              name-field="name"
              value-field="games_count"
              value-label="Игр"
            />
          </b-col>
          <b-col lg="4" md="6">
            <dashboard-top-table
              title="Топ пакетов по выручке"
              :rows="data.business && data.business.top_packs"
              name-field="pack_name"
              value-field="revenue"
              value-label="Выручка"
              value-format="rub"
            />
          </b-col>
          <b-col lg="4" md="12">
            <dashboard-author-tasks :author-tasks="data.business && data.business.author_tasks" />
          </b-col>
        </b-row>

        <!-- Ряд 5: рост контента -->
        <dashboard-content-growth :growth="data.content && data.content.growth" :totals="data.content && data.content.totals" />

        <!-- Ряд 6: покрытие / долги / Telegram -->
        <b-row class="match-height">
          <b-col lg="4" md="6">
            <dashboard-coverage :coverage="data.content && data.content.coverage" />
          </b-col>
          <b-col lg="4" md="6">
            <dashboard-debts :debts="data.content && data.content.debts" :generator="data.content && data.content.generator" />
          </b-col>
          <b-col lg="4" md="12">
            <dashboard-telegram :telegram="data.telegram" />
          </b-col>
        </b-row>

        <!-- Ряд 7: комментарии + здоровье -->
        <b-row class="match-height">
          <b-col lg="6">
            <dashboard-latest-comments :comments="data.feedback && data.feedback.latest_comments" />
          </b-col>
          <b-col lg="6">
            <dashboard-health :queues="data.queues" :sentry="data.sentry" />
          </b-col>
        </b-row>
      </template>
    </b-overlay>
  </section>
</template>

<script>
import {
  BRow, BCol, BOverlay, BButton, BSpinner,
} from 'bootstrap-vue'
import {
  ref, computed, onMounted, onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dashboardStoreModule from '@/views/dashboard/sifnf/dashboardStoreModule'
import DashboardProfitMedal from '@/views/dashboard/sifnf/components/DashboardProfitMedal.vue'
import DashboardKpiCards from '@/views/dashboard/sifnf/components/DashboardKpiCards.vue'
import DashboardUpcomingGames from '@/views/dashboard/sifnf/components/DashboardUpcomingGames.vue'
import DashboardWeeklyChart from '@/views/dashboard/sifnf/components/DashboardWeeklyChart.vue'
import DashboardPlanFact from '@/views/dashboard/sifnf/components/DashboardPlanFact.vue'
import DashboardRetentionChart from '@/views/dashboard/sifnf/components/DashboardRetentionChart.vue'
import DashboardFeedback from '@/views/dashboard/sifnf/components/DashboardFeedback.vue'
import DashboardTopTable from '@/views/dashboard/sifnf/components/DashboardTopTable.vue'
import DashboardAuthorTasks from '@/views/dashboard/sifnf/components/DashboardAuthorTasks.vue'
import DashboardContentGrowth from '@/views/dashboard/sifnf/components/DashboardContentGrowth.vue'
import DashboardCoverage from '@/views/dashboard/sifnf/components/DashboardCoverage.vue'
import DashboardDebts from '@/views/dashboard/sifnf/components/DashboardDebts.vue'
import DashboardTelegram from '@/views/dashboard/sifnf/components/DashboardTelegram.vue'
import DashboardLatestComments from '@/views/dashboard/sifnf/components/DashboardLatestComments.vue'
import DashboardHealth from '@/views/dashboard/sifnf/components/DashboardHealth.vue'

const STORE_MODULE_NAME = 'app-dashboard'
// Кнопка ставит флаг на сервере; сборку делает планировщик (≤1 мин до старта), поэтому поллим статус до смены updated_at.
const REFRESH_POLL_INTERVAL_MS = 5000
const REFRESH_POLL_TIMEOUT_MS = 15 * 60 * 1000

export default {
  components: {
    BRow, BCol, BOverlay, BButton, BSpinner,
    DashboardProfitMedal, DashboardKpiCards, DashboardUpcomingGames, DashboardWeeklyChart,
    DashboardPlanFact,
    DashboardRetentionChart, DashboardFeedback, DashboardTopTable, DashboardAuthorTasks,
    DashboardContentGrowth, DashboardCoverage, DashboardDebts, DashboardTelegram,
    DashboardLatestComments, DashboardHealth,
  },
  setup() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, dashboardStoreModule)

    const toast = useToast()
    const data = ref(null)
    const isLoading = ref(false)
    let isActive = true

    const cachedAt = computed(() => store.state[STORE_MODULE_NAME].cachedAt)
    const isRefreshing = computed(() => store.state[STORE_MODULE_NAME].isRefreshing)

    const formatCachedAt = iso => new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    let pollTimer = null
    const stopPolling = () => {
      if (pollTimer) clearTimeout(pollTimer)
      pollTimer = null
    }
    const finishRefresh = () => {
      stopPolling()
      store.commit(`${STORE_MODULE_NAME}/setRefreshing`, false)
    }

    const pollUntilUpdated = (previousUpdatedAt, startedAt) => {
      pollTimer = setTimeout(() => {
        store.dispatch(`${STORE_MODULE_NAME}/fetchCacheStatus`)
          .then(response => {
            if (!isActive) return
            const status = (response.data && response.data.data) || {}
            if (status.updated_at && status.updated_at !== previousUpdatedAt) {
              finishRefresh()
              load().then(success => {
                if (!isActive || !success) return
                toast({
                  component: ToastificationContent,
                  props: { title: 'Данные дэшборда обновлены', icon: 'CheckCircleIcon', variant: 'success' },
                })
              })
              return
            }
            if (Date.now() - startedAt > REFRESH_POLL_TIMEOUT_MS) {
              finishRefresh()
              toast({
                component: ToastificationContent,
                props: { title: 'Обновление затянулось — попробуйте перезагрузить страницу позже', icon: 'AlertTriangleIcon', variant: 'warning' },
              })
              return
            }
            pollUntilUpdated(previousUpdatedAt, startedAt)
          })
          .catch(() => {
            if (!isActive) return
            pollUntilUpdated(previousUpdatedAt, startedAt)
          })
      }, REFRESH_POLL_INTERVAL_MS)
    }

    const load = () => {
      isLoading.value = true
      return store.dispatch(`${STORE_MODULE_NAME}/fetchDashboard`)
        .then(response => {
          if (!isActive) return false
          data.value = response.data.data
          if (response.data.refreshing && !pollTimer && !isRefreshing.value) {
            store.commit(`${STORE_MODULE_NAME}/setRefreshing`, true)
            pollUntilUpdated(response.data.cached_at || cachedAt.value, Date.now())
          }
          return true
        })
        .catch(err => {
          if (!isActive) return false
          showError(err.response?.data?.message || 'Не удалось загрузить дэшборд')
          return false
        })
        .finally(() => { isLoading.value = false })
    }

    const refresh = () => {
      if (isRefreshing.value) return
      store.commit(`${STORE_MODULE_NAME}/setRefreshing`, true)
      store.dispatch(`${STORE_MODULE_NAME}/requestRefresh`)
        .then(response => {
          if (!isActive) return
          const status = (response.data && response.data.data) || {}
          pollUntilUpdated(status.updated_at || cachedAt.value, Date.now())
        })
        .catch(err => {
          if (!isActive) return
          finishRefresh()
          showError(err.response?.data?.message || 'Не удалось запросить обновление данных')
        })
    }

    onMounted(load)
    onUnmounted(() => {
      isActive = false
      stopPolling()
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    return {
      data, isLoading, cachedAt, isRefreshing, formatCachedAt, refresh,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/chart-apex.scss';

.dashboard-cache-status {
  white-space: nowrap;
}
</style>
